/**
 * Helpers for the admin-impersonation flow on the partners-app side.
 *
 * - decodeImpersonationActor: extract the `actor` claim from a partner JWT
 *   without verifying the signature. Verification happens on the server with
 *   every authenticated request; we only decode here to render the banner.
 * - readImpersonationCodeFromUrl: parse the one-time handoff code from the
 *   /impersonate route's query string.
 */

export interface ImpersonationActor {
  adminEmail: string;
  impersonationEventId: string;
}

/**
 * Decode the JWT payload (middle segment, base64url) and return the actor
 * claim if it's well-formed. Returns undefined for normal partner tokens
 * or anything malformed. Never throws.
 *
 * Important: this does NOT verify the signature. Treat the returned value
 * as display-only — every real authorization check happens server-side.
 */
export function decodeImpersonationActor(
  token: string,
): ImpersonationActor | undefined {
  const segments = token.split(".");
  if (segments.length !== 3) {
    return undefined;
  }
  const payload = segments[1];
  if (!payload) {
    return undefined;
  }
  let decoded: unknown;
  try {
    const json = atobUrl(payload);
    decoded = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (typeof decoded !== "object" || decoded === null) {
    return undefined;
  }
  const actor = (decoded as { actor?: unknown }).actor;
  if (typeof actor !== "object" || actor === null) {
    return undefined;
  }
  const adminEmail = (actor as { adminEmail?: unknown }).adminEmail;
  const impersonationEventId = (actor as { impersonationEventId?: unknown })
    .impersonationEventId;
  if (typeof adminEmail !== "string" || adminEmail.length === 0) {
    return undefined;
  }
  if (
    typeof impersonationEventId !== "string" ||
    impersonationEventId.length === 0
  ) {
    return undefined;
  }
  return { adminEmail, impersonationEventId };
}

function atobUrl(input: string): string {
  // base64url → base64; pad to multiple of 4.
  const base64 =
    input.replace(/-/g, "+").replace(/_/g, "/") +
    "===".slice(0, (4 - (input.length % 4)) % 4);
  return atob(base64);
}

/**
 * Read the `code` query param from the current location. Returns undefined
 * if missing or empty. The exchange endpoint expects the same value the
 * admin RPC put in the URL — base64url of the random 32-byte code.
 */
export function readImpersonationCodeFromUrl(
  search: string,
): string | undefined {
  const params = new URLSearchParams(search);
  const code = params.get("code");
  if (typeof code !== "string" || code.length === 0) {
    return undefined;
  }
  return code;
}
